import { useCallback } from "react";
import type { FilterGroup } from "@/lib/filters";
import { EMPTY_GROUP } from "@/lib/filters";
import { useTrackedSessionStorage } from "./use-tracked-browser-storage";

// Storage version for schema compatibility
const STORAGE_VERSION = "v1";

interface WebpageFiltersState {
  account: FilterGroup;
  opportunity: FilterGroup;
  touchpoint: FilterGroup;
}

const defaultState: WebpageFiltersState = {
  account: EMPTY_GROUP,
  opportunity: EMPTY_GROUP,
  touchpoint: EMPTY_GROUP,
};

/**
 * Hook to manage webpage filter state for Account, Opportunity & Touchpoint objects.
 *
 * - Persists state to sessionStorage for tab-specific isolation.
 * - Provides setters and clear helpers for each individual object type.
 * - Exposes convenience helpers for serialising the filters into the single
 *   `filters` query-parameter understood by the backend.
 */
export function useWebpageFilterState(scope: string) {
  // Use sessionStorage for tab-specific filter isolation
  // Migrated from filters::v1 to metrics::filters::v1 for clarity
  const storageKey = `${scope}::metrics::filters::${STORAGE_VERSION}`;

  // ---------------------------------------------------------------------------
  // Persistence layer
  // ---------------------------------------------------------------------------

  const [state, setState] = useTrackedSessionStorage<WebpageFiltersState>(
    storageKey,
    defaultState,
    {
      // Migrate from old location
      migration: {
        from: {
          key: `${scope}::filters::${STORAGE_VERSION}`,
          storage: "sessionStorage",
          organizationScoped: true,
        },
        cleanup: true, // Remove the old key after migration
      },
    },
  );

  // Get current filters with defaults
  const accountFilters = state?.account || EMPTY_GROUP;
  const opportunityFilters = state?.opportunity || EMPTY_GROUP;
  const touchpointFilters = state?.touchpoint || EMPTY_GROUP;

  // Check if any filters are active
  const hasAccountFilters = accountFilters.filters.length > 0;
  const hasOpportunityFilters = opportunityFilters.filters.length > 0;
  const hasTouchpointFilters = touchpointFilters.filters.length > 0;

  // Setters for each filter type
  const setAccountFilters = useCallback(
    (filters: FilterGroup) =>
      setState((prev) => ({
        account: filters,
        opportunity: prev?.opportunity || EMPTY_GROUP,
        touchpoint: prev?.touchpoint || EMPTY_GROUP,
      })),
    [setState],
  );

  const setOpportunityFilters = useCallback(
    (filters: FilterGroup) =>
      setState((prev) => ({
        account: prev?.account || EMPTY_GROUP,
        opportunity: filters,
        touchpoint: prev?.touchpoint || EMPTY_GROUP,
      })),
    [setState],
  );

  const setTouchpointFilters = useCallback(
    (filters: FilterGroup) =>
      setState((prev) => ({
        account: prev?.account || EMPTY_GROUP,
        opportunity: prev?.opportunity || EMPTY_GROUP,
        touchpoint: filters,
      })),
    [setState],
  );

  const clearAccountFilters = useCallback(() => {
    setState((prev) => ({
      account: EMPTY_GROUP,
      opportunity: prev?.opportunity || EMPTY_GROUP,
      touchpoint: prev?.touchpoint || EMPTY_GROUP,
    }));
  }, [setState]);

  const clearOpportunityFilters = useCallback(() => {
    setState((prev) => ({
      account: prev?.account || EMPTY_GROUP,
      opportunity: EMPTY_GROUP,
      touchpoint: prev?.touchpoint || EMPTY_GROUP,
    }));
  }, [setState]);

  const clearTouchpointFilters = useCallback(() => {
    setState((prev) => ({
      account: prev?.account || EMPTY_GROUP,
      opportunity: prev?.opportunity || EMPTY_GROUP,
      touchpoint: EMPTY_GROUP,
    }));
  }, [setState]);

  const clearAllFilters = useCallback(() => {
    setState(defaultState);
  }, [setState]);

  return {
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    setAccountFilters,
    setOpportunityFilters,
    setTouchpointFilters,
    clearAccountFilters,
    clearOpportunityFilters,
    clearTouchpointFilters,
    clearAllFilters,
  };
}
